import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Slide,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DiscardChangesDialog, {
  joinSectionLabels
} from './DiscardChangesDialog';
import { unloadWithUnsavedWorkAgreed } from 'src/store/workspace/unsavedWork';
import { usePedigreeInstance } from '../pedigree-tool/pedigree/instanceContext';
import {
  SAVE_FOOTER_FALLBACK_HEIGHT,
  SAVE_FOOTER_OFFSET_VAR
} from './saveFooterOffset';
import { useAutoTranslation } from 'src/hooks/useTranslate';

/**
 * Sticky save footer for the pedigree sidebar.
 *
 * Props:
 *   dirtyCount        — number of dirty sections
 *   dirtySectionLabels — human-readable names of dirty sections
 *   onSave            — async () => void — called when "Save changes" clicked
 *   onDiscard         — () => void — called when discard is confirmed
 *   liftsLockBanner   — this footer sits where the lock request banners
 *                       would cover it (saveFooterOffset.js), so while it is
 *                       showing it publishes its height for them to clear
 */
export default function SaveFooter({
  dirtyCount,
  dirtySectionLabels,
  onSave,
  onDiscard,
  liftsLockBanner = false
}) {
  const t = useAutoTranslation();

  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const footerRef = useRef(null);
  const { containerRef } = usePedigreeInstance();

  // Navigation guard — warn on page unload when there are unsaved changes
  useEffect(() => {
    if (dirtyCount === 0) return;
    const handler = (e) => {
      // Not a second time for a reload the clinician has already agreed to in
      // the app's own prompt (switching user group).
      if (unloadWithUnsavedWorkAgreed()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyCount]);

  const handleSave = async () => {
    if (saving || dirtyCount === 0) return;
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardConfirm = () => {
    setDiscardOpen(false);
    onDiscard();
  };

  const visible = dirtyCount > 0 || saving;

  // Lift the lock banners clear of Discard and Save while they are showing
  // (saveFooterOffset.js says where, and why on the tab's container), and
  // drop them back as soon as the footer starts to go. A passive effect, not
  // a layout one: the tab's container is an ANCESTOR, and React attaches an
  // ancestor's ref only after its descendants' layout effects have run, so a
  // layout effect here would miss the container on a first render and write
  // to the whole page instead. The footer slides in over a couple of hundred
  // milliseconds, so the frame this costs is not visible.
  const publishOffset = liftsLockBanner && visible;
  useEffect(() => {
    if (!publishOffset) return undefined;
    const target = containerRef?.current || document.documentElement;
    const footer = footerRef.current;
    const measure = () => {
      const height = Math.ceil(footer?.getBoundingClientRect().height || 0);
      target.style.setProperty(
        SAVE_FOOTER_OFFSET_VAR,
        `${height || SAVE_FOOTER_FALLBACK_HEIGHT}px`
      );
    };
    measure();
    const observer =
      footer && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measure)
        : null;
    observer?.observe(footer);
    return () => {
      observer?.disconnect();
      target.style.removeProperty(SAVE_FOOTER_OFFSET_VAR);
    };
  }, [publishOffset, containerRef]);

  return (
    <>
      <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
        <Box
          ref={footerRef}
          data-testid="save-footer"
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <Button
            variant="outlined"
            size="medium"
            color="inherit"
            disabled={saving}
            onClick={() => setDiscardOpen(true)}
          >
            {t('Discard')}
          </Button>

          <Button
            variant="contained"
            size="medium"
            color="primary"
            disabled={saving}
            onClick={handleSave}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
          >
            {t('Save')}
          </Button>

          <Typography
            variant="body2"
            sx={{
              ml: 0.5,
              color: 'warning.dark',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {dirtyCount === 1
              ? t('{{count}} unsaved section', { count: dirtyCount })
              : t('{{count}} unsaved sections', { count: dirtyCount })}
          </Typography>
        </Box>
      </Slide>
      {/* Discard confirmation dialog. Shared with the tab strip's close
          guard; `dense` keeps the panel-sized text and buttons it always had. */}
      <DiscardChangesDialog
        dense
        open={discardOpen}
        onCancel={() => setDiscardOpen(false)}
        onConfirm={handleDiscardConfirm}
      >
        {dirtySectionLabels.length > 0 ? (
          <>
            {t('Unsaved changes to')}{' '}
            <strong>{joinSectionLabels(dirtySectionLabels, t)}</strong>{' '}
            {t('will be lost.')}
          </>
        ) : (
          t('All unsaved changes will be lost.')
        )}
      </DiscardChangesDialog>
    </>
  );
}
